import { PermissionCode } from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { KbStatusBadge, KbVisibilityBadge } from '@/products/helpdesk/features/kb/kb-badges';
import {
  useArchiveKbArticle,
  useKbArticle,
  useLinkKbTicket,
  usePublishKbArticle,
  useUnlinkKbTicket,
} from '@/products/helpdesk/features/kb/use-kb';
import { ApiRequestError } from '@/lib/api';
import { PersonChip } from '@/platform/components/person-chip';
import { formatDateTime } from '@/lib/format';

export function KbArticlePage() {
  const { slug = '' } = useParams();
  const me = useMe();
  const article = useKbArticle(slug);
  const canWrite = hasPermission(me.data, PermissionCode.KB_WRITE);
  const canPublish = hasPermission(me.data, PermissionCode.KB_PUBLISH);
  const publish = usePublishKbArticle(slug);
  const archive = useArchiveKbArticle(slug);
  const linkTicket = useLinkKbTicket(slug);
  const unlinkTicket = useUnlinkKbTicket(slug);
  const [ticketNumber, setTicketNumber] = useState('');

  if (article.isPending) {
    return (
      <p className="flex items-center gap-2 text-muted-foreground">
        <Loader2Icon className="size-4 animate-spin" aria-hidden />
        Artikel wird geladen …
      </p>
    );
  }

  if (article.isError || !article.data) {
    return (
      <section className="space-y-3">
        <h1 className="text-2xl font-bold">Artikel nicht gefunden</h1>
        <Button asChild variant="outline">
          <Link to="/helpdesk/wissen">Zur Übersicht</Link>
        </Button>
      </section>
    );
  }

  const data = article.data;

  async function onPublish() {
    try {
      await publish.mutateAsync();
      toast.success('Artikel veröffentlicht.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError ? error.message : 'Veröffentlichen fehlgeschlagen.',
      );
    }
  }

  async function onArchive() {
    try {
      await archive.mutateAsync();
      toast.success('Artikel archiviert.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Archivieren fehlgeschlagen.');
    }
  }

  async function onLink() {
    const number = Number(ticketNumber.replace(/^#/, ''));
    if (!Number.isInteger(number) || number < 1) {
      toast.error('Bitte eine gültige Ticketnummer eingeben.');
      return;
    }
    try {
      await linkTicket.mutateAsync({ ticketNumber: number });
      setTicketNumber('');
      toast.success('Ticket verknüpft.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Verknüpfung fehlgeschlagen.');
    }
  }

  return (
    <section className="mx-auto max-w-3xl space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Button asChild variant="ghost" size="sm">
            <Link to="/helpdesk/wissen">Alle Artikel</Link>
          </Button>
          <h1 className="text-2xl font-bold tracking-tight">{data.title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            <span className="inline-flex flex-wrap items-center gap-1.5">
              <span>{data.category?.name ?? 'Ohne Kategorie'}</span>
              <span>·</span>
              <PersonChip
                displayName={data.author.displayName}
                profilePhotoUrl={data.author.profilePhotoUrl}
                avatarClassName="size-5"
              />
              <span>· Version {data.currentVersion}</span>
            </span>
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <KbStatusBadge status={data.status} />
            <KbVisibilityBadge visibility={data.visibility} />
            {data.tags.map((tag) => (
              <span key={tag.id} className="text-xs text-muted-foreground">
                #{tag.name}
              </span>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canWrite && (
            <>
              <Button asChild variant="outline" size="sm">
                <Link to={`/helpdesk/wissen/assistent?artikel=${data.slug}`}>Mit KI ändern</Link>
              </Button>
              <Button asChild variant="outline" size="sm">
                <Link to={`/helpdesk/wissen/${data.slug}/bearbeiten`}>Bearbeiten</Link>
              </Button>
            </>
          )}
          <Button asChild variant="outline" size="sm">
            <Link to={`/helpdesk/wissen/${data.slug}/historie`}>Historie</Link>
          </Button>
          {canPublish && data.status !== 'PUBLISHED' && (
            <Button size="sm" onClick={() => void onPublish()} disabled={publish.isPending}>
              Veröffentlichen
            </Button>
          )}
          {canPublish && data.status === 'PUBLISHED' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => void onArchive()}
              disabled={archive.isPending}
            >
              Archivieren
            </Button>
          )}
        </div>
      </div>

      {data.summary && <p className="text-muted-foreground">{data.summary}</p>}
      <div
        className="ticket-prose rounded-md border p-4"
        dangerouslySetInnerHTML={{ __html: data.body }}
      />

      <Card>
        <CardHeader>
          <CardTitle>Ursprungstickets</CardTitle>
          <CardDescription>Artikel, die aus konkreten Tickets entstanden sind.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {data.tickets.length === 0 && (
            <p className="text-sm text-muted-foreground">Keine Tickets verknüpft.</p>
          )}
          <ul className="space-y-1 text-sm">
            {data.tickets.map((ticket) => (
              <li key={ticket.id} className="flex items-center justify-between gap-2">
                <Link
                  className="text-primary hover:underline"
                  to={`/helpdesk/tickets/${ticket.number}`}
                >
                  #{ticket.number} · {ticket.subject}
                </Link>
                {canWrite && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => void unlinkTicket.mutateAsync(ticket.id)}
                  >
                    Entfernen
                  </Button>
                )}
              </li>
            ))}
          </ul>
          {canWrite && (
            <div className="flex items-end gap-2">
              <div className="space-y-1.5">
                <Label htmlFor="link-ticket">Ticketnummer</Label>
                <Input
                  id="link-ticket"
                  value={ticketNumber}
                  onChange={(event) => setTicketNumber(event.target.value)}
                  placeholder="42"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => void onLink()}
                disabled={linkTicket.isPending}
              >
                Verknüpfen
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        Aktualisiert {formatDateTime(data.updatedAt)}
        {data.hasEmbedding ? ' · Semantischer Index vorhanden' : ' · Embedding ausstehend'}
      </p>
    </section>
  );
}
